import React, { ReactElement, useEffect, useState } from 'react';
import { SetField } from './SetField';
import { NamedEntity } from '../../../types/entities/NamedEntity';
import { FieldComponent } from '../../../types/FieldComponent';
import { Status } from '../../../types/entities/Status';
import { compareText } from '../comparators/compareText';
import { SchemaWithClient } from '../../../types/SchemaWithClient';
import {
  Autocomplete,
  Button,
  Chip, Stack,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { SlidingPaneAddNew } from '../../Page/SlidingPaneAddNew';
import { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import LargePopper from '../assets/LargePopper';
import * as Icons from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";

type Props<T extends NamedEntity> = {
  onChange: (value: T) => void;
  schema: any;
  defaultValue: any;
  forcedValue: any;
  disabled: boolean;
  isMissing: boolean;
  accordingToValue: any;
  title?: string;
};

export const SPSelectIconField = <T extends NamedEntity>({
  onChange,
  defaultValue,
  forcedValue,
  disabled,
  isMissing,
  schema,
  accordingToValue,
  title,
}: Props<T>): ReactElement | null => {
  const [addingEntity, setAddingEntity] = useState(false);
  const [selectOptions, setSelectOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [currentState, setCurrentState] = useState<any>({
    value: null,
    send: false,
  });

  useEffect(() => {
    if (
      typeof defaultValue !== undefined &&
      defaultValue !== null &&
      defaultValue !== undefined
    ) {
      setSelectOptions([defaultValue]);
    }
  }, []);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setCurrentState({
        value: defaultValue,
        send: false,
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (currentState.send) {
      onChange(currentState.value);
    }
  }, [currentState]);

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      await fetchSelectOptions('');
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setSelectOptions([]);
  };

  const fetchSelectOptions = async (searchText: string) => {
    setLoading(true);

    let string: any = {
      searchValue: searchText,
    };

    if (typeof accordingToValue === 'object') {
      string.accordingToLabel = accordingToValue?.name;
      string.accordingToId = accordingToValue?.id;
      string.accordingToValue = accordingToValue?.value;
      string.withName = true;
    }
    const searchParams = new URLSearchParams(string);

    schema.client.readNames(searchParams).then((result: any) => {
      if (result?.data) {
        result.data.unshift({ id: 0, name: '', isButton: true });
        setSelectOptions(result.data);
      } else {
        result.unshift({ id: 0, name: '', isButton: true });
        setSelectOptions(result);
      }
      setLoading(false);
    });
  };

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    if (isInitialized) {
      fetchSelectOptions(value);
    } else {
      setIsInitialized(true);
    }
    //fetchSelectOptions(value);
  };
  const debouncedOnChange = debounce(handleInputChange, 300);

  const handleOnChange = (event: any, value: any) => {
    if (value?.isButton) {
      setAddingEntity(true);
    } else {
      setCurrentState({
        value: value,
        send: true,
      });
    }
  };

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        fullWidth={true}
        PopperComponent={LargePopper}
        size="small"
        loading={loading}
        options={selectOptions}
        renderOption={(props: any, option: any) => {

          const IconComponent = Icons[option?.name as keyof typeof Icons];

          return (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              {option?.isButton ? (
                <Button
                  startIcon={<AddIcon />}
                  size={'small'}
                  variant="contained"
                  fullWidth
                >
                  Add new
                </Button>
              ) : (
                <>
                 <Stack direction={'row'} gap={1} alignItems={'center'}>
                   <Avatar
                     sx={{
                       background: '#3a65a6',
                       width: 30,
                       height: 30,
                       '& .MuiSvgIcon-root':{
                         fontSize: '26px'
                       }
                     }}
                   >
                     <>{IconComponent ? <IconComponent /> : null}</>
                   </Avatar>
                   {option?.name}
                 </Stack>
                </>
              )}
            </Box>
          );
        }}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option?.name}
        isOptionEqualToValue={(option, value) => {
          if (!option?.id) return false;
          return option.id === value.id;
        }}
        value={currentState.value}
        freeSolo
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              size="small"
              label={title}
              InputLabelProps={{
                style: {
                  fontSize: 13,
                  paddingTop: 2,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onChange={(event, newValue) => {
          handleOnChange(event, newValue);
        }}
        onInputChange={debouncedOnChange}
        disabled={disabled}
      />
      {addingEntity && (
        <SlidingPaneAddNew
          schema={schema}
          mainSchema={schema}
          id={'new'}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            if (value) {
              setCurrentState({
                value: value,
                send: true,
              });
              setAddingEntity(false);
            }
          }}
          propertiesFromTable={{}}
          row={{}}
          onClose={(value) => {
            setAddingEntity(false);
          }}
        />
      )}
    </>
  );
};

SPSelectIconField.tableAdapter = <T extends NamedEntity = NamedEntity>(
  schema: SchemaWithClient<T>
) => {
  const Field: FieldComponent<T, T[]> = ({
    onChange,
    isActive: active,
    defaultValue,
    forcedValue,
    disabled,
    isMissing,
    accordingToValue,
    title,
  }) => {
    return (
      <SPSelectIconField
        onChange={onChange}
        defaultValue={defaultValue}
        forcedValue={forcedValue}
        disabled={!!disabled}
        isMissing={!!isMissing}
        schema={schema}
        accordingToValue={accordingToValue}
        title={title}
      />
    );
  };

  Field.includes = (query = [], value) =>
    value
      ? query.length === 0 || query.some((item) => value.id === item.id)
      : false;
  Field.comparator = (one, other) => compareText(one?.name, other?.name);
  Field.FilterRenderer = SetField.tableAdapter(schema).FilterRenderer;

  return Field;
};

function isStatusValue(value: NamedEntity): value is Status {
  if (typeof value === 'string' || value === null) {
    return false;
  }
  return 'color' in value;
}

function isStatusLamp(value: NamedEntity): value is Status {
  return 'status_lamp' in value;
}
